import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { BarChart3, Bell, BellOff, Check, ChevronLeft, ChevronRight, Compass, Loader2, LocateFixed, MapPin, Moon, Search, Settings, Sun } from 'lucide-react';
import { PRAYERS, PRAYER_LABEL, addDays, dayKeyIn, fmtTime, forbiddenTimes, hijriOn, nextPrayer, qiblaBearing, timesFor, type PrayerKey } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import type { PrayerDay, PrayerStatus } from '@/lib/types';
import { Card, IconButton, LargeTitle, Row, Section, Sheet, Stepper, haptic } from '@/components/ui';
import { SUNNAH, sunnahDef, type SunnahDef } from './sunnah';
import { PrayerTimer } from './PrayerTimer';
import { phaseAt } from '@/components/islamic/Scene';
import { Backdrop } from '@/components/islamic/Backdrop';
import { WorshipSwitch } from './WorshipSwitch';
import { PeriodBanner } from '@/features/fasting/PeriodControls';
import { periodOn } from '@/features/fasting/fastMetrics';
import { currentWindow, fmtUntil, statusForLevel } from './window';

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

/* Selectors must return stable references — a fresh `{}` each render makes
   zustand re-render forever. */
const NO_DAY: PrayerDay = { dayKey: '', marks: {}, updatedAt: 0 };


type Fard = Exclude<PrayerKey, 'sunrise'>;

/* Same four colours as the stats charts (--st-*, validated for colour-blind
   separation); the label always travels with the colour. */
export const STATUS: { value: PrayerStatus; label: string; color: string }[] = [
  { value: 'jamaah', label: 'In jamāʿah', color: 'var(--st-jamaah)' },
  { value: 'ontime', label: 'On time', color: 'var(--st-ontime)' },
  { value: 'late', label: 'Late', color: 'var(--st-late)' },
  { value: 'qada', label: 'Made up (qaḍāʾ)', color: 'var(--st-late)' },
  { value: 'missed', label: 'Missed', color: 'var(--st-missed)' },
];

export const dayLabel = (dayKey: string, today: string) => {
  if (dayKey === today) return 'Today';
  if (dayKey === addDays(today, -1)) return 'Yesterday';
  return new Date(`${dayKey}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
};

export function PrayerPage() {
  const { prayer, hour12, hijriOffset, notify, trackSunnah, gender } = useSettings((s) => s.settings);
  const updateDeep = useSettings((s) => s.updateDeep);
  const now = useNow();
  const today = dayKeyIn(prayer.tz, now);
  const [params, setParams] = useSearchParams();
  const requested = params.get('day');
  const dayKey = requested && /^\d{4}-\d{2}-\d{2}$/.test(requested) && requested <= today ? requested : today;
  const isToday = dayKey === today;
  const setDay = (d: string) => { haptic(); setParams(d >= today ? {} : { day: d }, { replace: true }); };

  const times = useMemo(() => timesFor(prayer, dayKey), [prayer, dayKey]);
  const prevNight = useMemo(() => timesFor(prayer, addDays(dayKey, -1)), [prayer, dayKey]);
  const next = nextPrayer(prayer, now);
  const hijri = hijriOn(dayKey, hijriOffset);
  const day = useProgress((s) => s.prayers[dayKey]) ?? NO_DAY;
  const excused = useProgress((s) => (gender === 'female' ? periodOn(s.periods, dayKey, today) : null));
  const markPrayer = useProgress((s) => s.markPrayer);
  const markSunnah = useProgress((s) => s.markSunnah);
  const [marking, setMarking] = useState<Fard | null>(null);
  const [rakatFor, setRakatFor] = useState<SunnahDef | null>(null);
  const [locOpen, setLocOpen] = useState(false);
  const [qiblaOpen, setQiblaOpen] = useState(false);

  const tracked = (w: SunnahDef['with']) => SUNNAH.filter((s) => s.with === w && trackSunnah[s.key]);
  const showTimer = isToday && !excused && !!currentWindow(prayer, now);
  const fardLogged = PRAYERS.filter((k) => k !== 'sunrise' && day.marks[k as Fard]).length;

  const toggleSunnah = (s: SunnahDef) => {
    if (s.options) { setRakatFor(s); return; }
    haptic(12);
    markSunnah(dayKey, s.key, day.sunnah?.[s.key] ? null : s.rakats);
  };

  const chips = (list: SunnahDef[]) => list.length ? (
    <div className="flex flex-wrap gap-2 pb-3 pl-[56px] pr-4">
      {list.map((s) => {
        const n = day.sunnah?.[s.key];
        return (
          <button key={s.key} type="button" aria-pressed={!!n} aria-label={`${s.name}${n ? `, ${n} rakʿahs logged` : ''}`} onClick={() => toggleSunnah(s)}
            className={`press flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold ${n ? 'bg-accent text-accent-ink' : 'bg-fill text-muted'}`}>
            {n ? <Check size={13} strokeWidth={3} /> : null}
            {s.short}{s.options && n ? ` · ${n}` : ''}
          </button>
        );
      })}
    </div>
  ) : null;

  const span = (a: Date, b: Date) => `${fmtTime(a, prayer.tz, hour12)} – ${fmtTime(b, prayer.tz, hour12)}`;
  const duhaStart = new Date(times.sunrise.getTime() + 15 * 60_000);
  const duhaEnd = new Date(times.dhuhr.getTime() - 10 * 60_000);

  const optionalRow = (key: 'duha' | 'tahajjud', title: React.ReactNode, sub: string, ar: string, icon: React.ReactNode) => {
    const n = day.sunnah?.[key];
    return (
      <div className="row">
        <button type="button" onClick={() => toggleSunnah(sunnahDef(key))} className="press flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className={`grid h-7 w-7 place-items-center rounded-full ${n ? 'bg-accent text-accent-ink' : 'border-2 border-dashed border-hairline'}`}>{n ? <Check size={15} strokeWidth={3} /> : icon}</span>
          <span className="flex-1">
            <span className="block text-[17px] font-medium">{title}</span>
            <span className="block text-[12px] text-muted">{n ? `${n} rakʿahs` : 'Optional'} · {sub}</span>
          </span>
          <span className="font-arabic text-[18px] text-muted">{ar}</span>
        </button>
      </div>
    );
  };

  return (
    <div className="page-pad">
      <LargeTitle title="Prayer" ar="الصلاة" subtitle={hijri ? `${hijri.d} ${hijri.month} ${hijri.y} AH` : undefined}
        right={<>
          <IconButton label="Prayer stats" to="/prayer/stats"><BarChart3 size={18} /></IconButton>
          <IconButton label="Qibla" onClick={() => setQiblaOpen(true)}><Compass size={18} /></IconButton>
          <IconButton label="Prayer settings" to="/settings/prayer"><Settings size={18} /></IconButton>
        </>} />
      <WorshipSwitch />

      {isToday && (
        <div className="px-4 pb-5">
          <Card className="relative overflow-hidden !p-0">
            <div className="absolute inset-0"><Backdrop phase={phaseAt(prayer, now)} height="100%" lift={showTimer ? 196 : 0} /></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/55" />
            <div className="relative p-5 text-white [&_.text-muted]:text-white/75">
            <button type="button" onClick={() => setLocOpen(true)} className="press flex items-center gap-1.5 text-[14px] font-semibold text-white/85"><MapPin size={14} /> {prayer.placeName || `${prayer.lat.toFixed(2)}, ${prayer.lng.toFixed(2)}`}</button>
            <div className="mt-3 text-[15px] text-white/80">{next.current && next.current !== 'sunrise' ? `${PRAYER_LABEL[next.current].en} now · ` : ''}Next</div>
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-[34px] font-bold leading-tight drop-shadow">{PRAYER_LABEL[next.key].en} <span className="font-arabic text-[24px] font-normal text-[#f3d27a]">{PRAYER_LABEL[next.key].ar}</span></div>
              <div className="text-[22px] font-semibold tabular-nums">{fmtTime(next.at, prayer.tz, hour12)}</div>
            </div>
            <div className="mt-1 text-[17px] font-semibold tabular-nums text-[#9ff0c9]">in {fmtUntil(next.at.getTime() - now.getTime())}</div>
            <div className={showTimer ? 'h-40' : 'h-28'} />
            {!excused && <div className="mt-4">
              <PrayerTimer settings={prayer} now={now} hour12={hour12} onLog={() => {
                const w = currentWindow(prayer, now);
                if (!w) return;
                haptic(15);
                // Logged as wherever the timer is now: green → on time, amber/red (or ʿIshāʾ after midnight) → late.
                markPrayer(w.dayKey, w.key, statusForLevel(w.level), now.getTime());
              }} />
            </div>}
            </div>
          </Card>
        </div>
      )}

      {/* Day navigator — step back to log earlier days, or pick a date */}
      {/* key: the header re-mounts (and flashes) whenever the day changes. */}
      <div key={dayKey} id="day-editor" className={`mx-2 flex items-center justify-between gap-2 px-2 py-1 pb-3 ${dayKey !== today ? 'day-flash' : ''}`} aria-live="polite">
        <button type="button" aria-label="Previous day" onClick={() => setDay(addDays(dayKey, -1))} className="press grid h-10 w-10 place-items-center rounded-full bg-fill"><ChevronLeft size={20} /></button>
        <div className="flex flex-col items-center">
          <label className="relative cursor-pointer text-[17px] font-bold">
            {dayLabel(dayKey, today)}
            <input type="date" aria-label="Choose a day" max={today} value={dayKey} onChange={(e) => e.target.value && setDay(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
          </label>
          <span className="text-[12px] text-muted">
            {fardLogged}/5 logged
            {!isToday && <> · <button type="button" className="font-semibold text-accent" onClick={() => setDay(today)}>Back to today</button></>}
          </span>
        </div>
        <button type="button" aria-label="Next day" disabled={isToday} onClick={() => setDay(addDays(dayKey, 1))} className="press grid h-10 w-10 place-items-center rounded-full bg-fill disabled:opacity-30"><ChevronRight size={20} /></button>
      </div>

      <PeriodBanner dayKey={dayKey} today={today} context="prayer" />

      <Section footer="Tap a prayer to log it, and the chips beneath it for its sunnah. Use the arrows or tap the date to log earlier days.">
        {PRAYERS.map((k) => {
          const isSalah = k !== 'sunrise';
          const mark = isSalah ? day.marks[k as Fard] : undefined;
          const st = STATUS.find((s) => s.value === mark);
          const isNext = isToday && next.key === k;
          const alertOn = isSalah && notify.adhan[k as Fard];
          return (
            <div key={k}>
              <div className={`row ${isNext ? 'bg-accent/10' : ''}`}>
                <button type="button" disabled={!isSalah} onClick={() => isSalah && setMarking(k as Fard)} className="press flex min-w-0 flex-1 items-center gap-3 text-left">
                  <span className={`grid h-7 w-7 place-items-center rounded-full text-[13px] font-bold ${st ? 'text-black' : 'border-2 border-hairline'} ${!isSalah ? 'invisible' : ''}`} style={st ? { background: st.color } : undefined}>
                    {st && (st.value === 'missed' ? '✕' : <Check size={15} strokeWidth={3} />)}
                  </span>
                  <span className="flex-1">
                    <span className={`block text-[17px] ${isNext ? 'font-bold' : 'font-medium'}`}>{PRAYER_LABEL[k].en}</span>
                    {st ? <span className="block text-[12px] text-muted">{st.label}</span> : excused && isSalah ? <span className="block text-[12px] text-[#e87ba4]">Excused</span> : null}
                  </span>
                  <span className="font-arabic text-[18px] text-muted">{PRAYER_LABEL[k].ar}</span>
                </button>
                <span className="w-[72px] text-right text-[17px] font-semibold tabular-nums">{fmtTime(times[k], prayer.tz, hour12)}</span>
                {isSalah ? (
                  <button type="button" aria-label={`${alertOn ? 'Disable' : 'Enable'} ${PRAYER_LABEL[k].en} adhan notification`}
                    onClick={() => { haptic(); updateDeep('notify', { adhan: { ...notify.adhan, [k]: !alertOn } }); }}
                    className={`press grid h-8 w-8 place-items-center rounded-full ${alertOn ? 'text-accent' : 'text-faint'}`}>
                    {alertOn ? <Bell size={17} /> : <BellOff size={17} />}
                  </button>
                ) : <span className="w-8" />}
              </div>
              {isSalah && chips(tracked(k as Fard))}
              {k === 'sunrise' && trackSunnah.duha && optionalRow('duha', 'Ḍuḥā', span(duhaStart, duhaEnd), 'الضحى', <Sun size={13} className="text-gold" />)}
            </div>
          );
        })}
      </Section>

      <Section title="Night">
        {trackSunnah.tahajjud && optionalRow('tahajjud', <>Tahajjud <span className="text-[13px] font-normal text-muted">· the night before</span></>, `best from ${fmtTime(prevNight.lastThird, prayer.tz, hour12)}`, 'التهجد', <Moon size={13} className="text-gold" />)}
        <Row label="Middle of the night" detail={fmtTime(times.midnight, prayer.tz, hour12)} />
        <Row label="Last third begins" detail={fmtTime(times.lastThird, prayer.tz, hour12)} />
      </Section>

      <Section title="Forbidden prayer times" footer="No voluntary prayer at these times (Muslim 831); a missed obligatory prayer may still be made up. Notifications: Settings → Notifications.">
        {forbiddenTimes(times).map((f) => (
          <Row key={f.key} label={f.label} detail={`${fmtTime(f.start, prayer.tz, hour12)} – ${fmtTime(f.end, prayer.tz, hour12)}`} />
        ))}
      </Section>

      <div className="px-4 pb-6">
        <Card to="/prayer/stats" className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-ink"><BarChart3 size={20} /></span>
          <span className="flex-1"><span className="block font-semibold">Prayer stats & streaks</span><span className="block text-[13px] text-muted">Weekly, monthly and yearly</span></span>
          <ChevronRight className="text-faint" />
        </Card>
      </div>

      <Sheet open={!!marking} onClose={() => setMarking(null)} title={marking ? `${PRAYER_LABEL[marking].en} · ${dayLabel(dayKey, today)}` : ''}>
        <Section>
          {STATUS.map((s) => (
            <Row key={s.value} label={s.label} onClick={() => { if (marking) { haptic(12); markPrayer(dayKey, marking, s.value); } setMarking(null); }}>
              <span className="h-4 w-4 rounded-full" style={{ background: s.color }} />
            </Row>
          ))}
          {marking && day.marks[marking] && <Row label="Clear" danger onClick={() => { markPrayer(dayKey, marking, null); setMarking(null); }} />}
        </Section>
      </Sheet>

      <RakatSheet def={rakatFor} current={rakatFor ? day.sunnah?.[rakatFor.key] : undefined} onClose={() => setRakatFor(null)}
        onSave={(n) => { if (rakatFor) { haptic(12); markSunnah(dayKey, rakatFor.key, n); } setRakatFor(null); }} />
      <LocationSheet open={locOpen} onClose={() => setLocOpen(false)} />
      <QiblaSheet open={qiblaOpen} onClose={() => setQiblaOpen(false)} />
    </div>
  );
}

function RakatSheet({ def, current, onClose, onSave }: { def: SunnahDef | null; current?: number; onClose: () => void; onSave: (n: number | null) => void }) {
  const [n, setN] = useState(2);
  useEffect(() => { if (def) setN(current ?? def.rakats); }, [def, current]);
  if (!def) return null;
  const opts = def.options ?? [def.rakats];
  return (
    <Sheet open onClose={onClose} title={def.name}>
      <p className="px-6 pb-3 text-[14px] text-muted">{def.note}</p>
      <div className="flex flex-wrap justify-center gap-2 px-4 pb-4" role="group" aria-label="Rakʿahs">
        {opts.map((o) => (
          <button key={o} type="button" aria-pressed={n === o} onClick={() => { haptic(); setN(o); }}
            className={`press h-12 min-w-12 rounded-2xl px-3 text-[17px] font-bold ${n === o ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>{o}</button>
        ))}
      </div>
      <div className="flex items-center justify-center gap-3 pb-4 text-[14px] text-muted">Other <Stepper value={n} min={1} max={20} onChange={setN} /> rakʿahs</div>
      <div className="flex gap-3 px-4">
        {current ? <button type="button" onClick={() => onSave(null)} className="press flex-1 rounded-2xl bg-fill py-3.5 font-semibold text-danger">Clear</button> : null}
        <button type="button" onClick={() => onSave(n)} className="press flex-[2] rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink">Log {n} rakʿahs</button>
      </div>
    </Sheet>
  );
}

/* City search via Open-Meteo geocoding (free, no key, CORS-enabled, returns the IANA zone). */
interface Place { name: string; country?: string; admin1?: string; latitude: number; longitude: number; timezone: string }

/** The city (and country) at a point. Two free services, so one being down or blocked doesn't
    leave you with "Current location"; null if neither answers. */
export const UNNAMED_PLACE = 'Current location';
export async function cityAt(lat: number, lng: number): Promise<string | null> {
  const country = (code?: string, name?: string) => {
    try { if (code) return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? name; } catch {}
    return name;
  };
  const join = (city?: string, cc?: string, cn?: string) => (city ? [city, country(cc, cn)].filter(Boolean).join(', ') : null);
  const get = async <T,>(url: string): Promise<T | null> => {
    try {
      const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
      return r.ok ? ((await r.json()) as T) : null;
    } catch { return null; }
  };
  const b = await get<{ city?: string; locality?: string; principalSubdivision?: string; countryCode?: string; countryName?: string }>(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
  const fromB = b && join(b.city || b.locality || b.principalSubdivision, b.countryCode, b.countryName);
  if (fromB) return fromB;
  const o = await get<{ address?: { city?: string; town?: string; village?: string; municipality?: string; county?: string; state?: string; country_code?: string; country?: string } }>(
    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&accept-language=en&lat=${lat}&lon=${lng}`);
  const a = o?.address;
  return a ? join(a.city || a.town || a.village || a.municipality || a.county || a.state, a.country_code, a.country) : null;
}

/** Mount once: a location saved without a name ("Current location", or none) gets its city
    looked up when the app opens or comes back online. */
export function useNamePlace() {
  const { lat, lng, placeName } = useSettings((s) => s.settings.prayer);
  const updateDeep = useSettings((s) => s.updateDeep);
  useEffect(() => {
    if (placeName && placeName !== UNNAMED_PLACE) return;
    let live = true;
    const run = () => void cityAt(lat, lng).then((name) => {
      const p = useSettings.getState().settings.prayer;
      if (live && name && p.lat === lat && p.lng === lng) updateDeep('prayer', { placeName: name });
    });
    run();
    window.addEventListener('online', run);
    return () => { live = false; window.removeEventListener('online', run); };
  }, [lat, lng, placeName, updateDeep]);
}

export function LocationSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const updateDeep = useSettings((s) => s.updateDeep);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Place[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q.trim())}&count=8&language=en&format=json`);
        const j = (await r.json()) as { results?: Place[] };
        setResults(j.results ?? []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const choose = (p: Place) => {
    updateDeep('prayer', { lat: p.latitude, lng: p.longitude, tz: p.timezone, placeName: [p.name, p.country].filter(Boolean).join(', ') });
    onClose();
  };

  const locate = () => {
    setBusy(true); setErr(null);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      // If no service answers (no signal), the name is filled in later — see useNamePlace.
      const placeName = (await cityAt(latitude, longitude)) ?? UNNAMED_PLACE;
      updateDeep('prayer', { lat: latitude, lng: longitude, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, placeName });
      setBusy(false); onClose();
    }, (e) => { setBusy(false); setErr(e.code === 1 ? 'Location permission was denied. Search for your city instead.' : 'Could not get your location.'); }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 });
  };

  return (
    <Sheet open={open} onClose={onClose} title="Location">
      <Section>
        <Row icon={busy ? <Loader2 size={18} className="animate-spin" /> : <LocateFixed size={18} />} label="Use my current location" onClick={locate} />
      </Section>
      {err && <p className="px-8 pb-3 text-[14px] text-danger">{err}</p>}
      <div className="px-4 pb-3">
        <label className="flex items-center gap-2 rounded-xl bg-fill px-3 py-2">
          <Search size={18} className="text-muted" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city" className="w-full bg-transparent text-[16px] outline-none" />
        </label>
      </div>
      {results.length > 0 && (
        <Section>
          {results.map((p) => (
            <Row key={`${p.latitude},${p.longitude}`} label={p.name} detail={[p.admin1, p.country].filter(Boolean).join(', ')} onClick={() => choose(p)} />
          ))}
        </Section>
      )}
    </Sheet>
  );
}

/** Great-circle distance to the Kaʿbah (21.4225° N, 39.8262° E), in km. */
function kmToKaabah(lat: number, lng: number) {
  const r = Math.PI / 180, dLat = (21.4225 - lat) * r, dLng = (39.8262 - lng) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat * r) * Math.cos(21.4225 * r) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function QiblaSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lat, lng, placeName } = useSettings((s) => s.settings.prayer);
  const bearing = qiblaBearing(lat, lng);
  const [heading, setHeading] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [locOpen, setLocOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    type IOSOrientation = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<'granted' | 'denied'> };
    const DOE = window.DeviceOrientationEvent as IOSOrientation | undefined;
    if (DOE?.requestPermission) { setNeedsPermission(true); return; }
    return listen();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function listen() {
    const onOri = (e: DeviceOrientationEvent & { webkitCompassHeading?: number; webkitCompassAccuracy?: number }) => {
      if (e.webkitCompassAccuracy != null) setAccuracy(e.webkitCompassAccuracy);
      if (e.webkitCompassHeading != null) setHeading(e.webkitCompassHeading);
      else if (e.absolute && e.alpha != null) setHeading(360 - e.alpha);
    };
    window.addEventListener('deviceorientationabsolute', onOri as EventListener);
    window.addEventListener('deviceorientation', onOri as EventListener);
    return () => { window.removeEventListener('deviceorientationabsolute', onOri as EventListener); window.removeEventListener('deviceorientation', onOri as EventListener); };
  }

  const enable = async () => {
    const DOE = window.DeviceOrientationEvent as unknown as { requestPermission: () => Promise<string> };
    if ((await DOE.requestPermission()) === 'granted') { setNeedsPermission(false); listen(); }
  };

  const rot = heading == null ? bearing : bearing - heading;
  const aligned = heading != null && Math.abs(((rot + 540) % 360) - 180) < 5;
  useEffect(() => { if (aligned) haptic(40); }, [aligned]);
  const km = kmToKaabah(lat, lng);
  // iPhone reports how far off its compass may be, in degrees; past ~20° it needs calibrating.
  const unsure = accuracy != null && (accuracy < 0 || accuracy > 20);

  return (
    <Sheet open={open} onClose={onClose} title="Qibla">
      <Section>
        <Row icon={<MapPin size={18} />} label={placeName || 'Set your location'} detail="Change" onClick={() => setLocOpen(true)} />
      </Section>
      <div className="flex flex-col items-center gap-4 px-6 pb-6 pt-2">
        <div className={`relative grid h-64 w-64 place-items-center rounded-full border-4 ${aligned ? 'border-accent' : 'border-hairline'} bg-surface`}>
          <div className="absolute inset-0 transition-transform duration-200" style={{ transform: `rotate(${heading == null ? 0 : -heading}deg)` }}>
            {['N', 'E', 'S', 'W'].map((d, i) => (
              <span key={d} className="absolute left-1/2 top-1/2 text-[13px] font-bold text-muted" style={{ transform: `rotate(${i * 90}deg) translateY(-112px) translateX(-50%)`, transformOrigin: '0 0' }}>{d}</span>
            ))}
          </div>
          <div className="absolute inset-6 transition-transform duration-200" style={{ transform: `rotate(${rot}deg)` }}>
            <div className="mx-auto h-1/2 w-1.5 rounded-full bg-gradient-to-t from-transparent to-gold" />
          </div>
          <span className="text-[34px]">🕋</span>
        </div>
        <div className="text-center">
          <div className="text-[22px] font-bold tabular-nums">{Math.round(bearing)}° from North</div>
          <div className={`text-[14px] ${aligned ? 'font-semibold text-accent' : 'text-muted'}`}>{heading == null ? 'Hold your phone flat. Without a compass (e.g. on a computer), face the bearing above.' : aligned ? 'You are facing the Qibla' : 'Hold your phone flat and turn until the arrow points up'}</div>
          <div className="mt-1 text-[13px] text-muted">{Math.round(km).toLocaleString('en-GB')} km to the Kaʿbah</div>
          {unsure && <div className="mt-2 text-[13px] text-[var(--win-warn)]">The compass is unsure. Move your phone in a figure of eight, away from metal and magnets.</div>}
        </div>
        {needsPermission && <button type="button" onClick={enable} className="press rounded-full bg-accent px-5 py-2.5 font-semibold text-accent-ink">Enable compass</button>}
      </div>
      <LocationSheet open={locOpen} onClose={() => setLocOpen(false)} />
    </Sheet>
  );
}
